import React, { useEffect, useRef, useState } from "react";
import DatePicker from "react-datepicker";
import { Calendar } from "lucide-react";
import "react-datepicker/dist/react-datepicker.css";
import { useCandidate } from "./useCandidate";
import { CandidateType } from "../../../store/types/candidate";
import { useJobs } from "../../jobs/hooks/useJobs";
import SubmitButton from "../../../components/elements/forms/SubmitButton";
import { toLocalISOString } from "../../../utility/candidateHelper";

interface CreateCandidateModalProps {
  candidateData?: CandidateType | null;
  isEditMode?: boolean;
  onSuccess?: () => void;
}

const RescheduleModal: React.FC<CreateCandidateModalProps> = ({
  candidateData,
  isEditMode = false,
  onSuccess,
}) => {
  const { data, error, onChangeHandler, onSubmit, setData, loading } = useCandidate(
    isEditMode,
    candidateData?.interviewId,
    onSuccess
  );
  const { jobs } = useJobs();

  const [hasInitialized, setHasInitialized] = useState(false);
  const [openPicker, setOpenPicker] = useState<"interview" | "expiration" | null>(null);

  const interviewPickerRef = useRef<HTMLDivElement | null>(null);
  const expirationPickerRef = useRef<HTMLDivElement | null>(null);

  // Handle clicks outside both datepickers
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        interviewPickerRef.current &&
        !interviewPickerRef.current.contains(event.target as Node) &&
        expirationPickerRef.current &&
        !expirationPickerRef.current.contains(event.target as Node)
      ) {
        setOpenPicker(null);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Parse to ISO format (preserve local time)
  const parseDateToISO = (dateString: string): string => {
    if (!dateString) return "";
    const date = new Date(dateString);
    return !isNaN(date.getTime()) ? toLocalISOString(date) : "";
  };

  // Initialize form for edit mode
  useEffect(() => {
    if (isEditMode && candidateData && jobs.length > 0 && !hasInitialized) {
      const interviewDate = parseDateToISO(candidateData.date ?? "");
      const expirationDate = parseDateToISO(candidateData.expirationDate ?? "");
      setData({
        interviewId: candidateData.interviewId || "",
        interviewDate,
        expirationDate,
      });
      setHasInitialized(true);
    }
  }, [isEditMode, candidateData, jobs, setData, hasInitialized]);

  const getSelectedDate = (value: string | undefined): Date | null => {
    if (!value) return null;
    const date = new Date(value);
    return isNaN(date.getTime()) ? null : date;
  };

  return (
    <div>
      <p className="text-20 font-bold mb-4 text-center">Reschedule Interview</p>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {/* Interview Date */}
        <div className="flex flex-col" ref={interviewPickerRef}>
          <label className="font-semibold text-[15px] text-[#1E1E1E] mb-1">
            Schedule Interview Date <span className="text-red-500">*</span>
          </label>
          <div
            className={`relative flex items-center w-full rounded-md border ${
              error.interviewDate ? "border-red-500" : "border-gray-300"
            }`}
          >
            <DatePicker
              selected={getSelectedDate(data.interviewDate)}
              onChange={(date) =>
                onChangeHandler({
                  target: {
                    name: "interviewDate",
                    value: date ? toLocalISOString(date) : "",
                  },
                } as React.ChangeEvent<HTMLInputElement>)
              }
              showTimeSelect
              timeFormat="HH:mm"
              timeIntervals={15}
              dateFormat="dd/MM/yyyy HH:mm"
              placeholderText="dd/mm/yyyy, --:--"
              className="w-full py-2 px-3 pr-10 rounded-md focus:outline-none text-gray-700 text-sm sm:text-base"
              open={openPicker === "interview"}
              onSelect={() => setOpenPicker(null)}
              popperClassName="!z-[9999]"
              popperPlacement="bottom-start"
              portalId="root-portal"

            />
            <Calendar
              className="absolute right-3 text-gray-400 cursor-pointer w-4 h-4 sm:w-5 sm:h-5"
              onClick={() =>
                setOpenPicker((prev) => (prev === "interview" ? null : "interview"))
              }
            />
          </div>
          {error.interviewDate && (
            <span className="text-red-500 text-xs mt-1">{error.interviewDate}</span>
          )}
        </div>

        {/* Expiration Date */}
        <div className="flex flex-col" ref={expirationPickerRef}>
          <label className="font-semibold text-[15px] text-[#1E1E1E] mb-1">
            Interview Expiration Date <span className="text-red-500">*</span>
          </label>
          <div
            className={`relative flex items-center w-full rounded-md border ${
              error.expirationDate ? "border-red-500" : "border-gray-300"
            }`}
          >
            <DatePicker
              selected={getSelectedDate(data.expirationDate)}
              onChange={(date) =>
                onChangeHandler({
                  target: {
                    name: "expirationDate",
                    value: date ? toLocalISOString(date) : "",
                  },
                } as React.ChangeEvent<HTMLInputElement>)
                
              }
              showTimeSelect
              timeFormat="HH:mm"
              timeIntervals={15}
              dateFormat="dd/MM/yyyy HH:mm"
              placeholderText="dd/mm/yyyy, --:--"
              className="w-full py-2 px-3 pr-10 rounded-md focus:outline-none text-gray-700 text-sm sm:text-base"
              open={openPicker === "expiration"}
              onSelect={() => setOpenPicker(null)}
              popperClassName="!z-[9999]"
              popperPlacement="bottom-start"
              portalId="root-portal"

            />
            <Calendar
              className="absolute right-3 text-gray-400 cursor-pointer w-4 h-4 sm:w-5 sm:h-5"
              onClick={() =>
                setOpenPicker((prev) => (prev === "expiration" ? null : "expiration"))
              }
            />
          </div>
          {error.expirationDate && (
            <span className="text-red-500 text-xs mt-1">{error.expirationDate}</span>
          )}
        </div>
      </div>

      <div className="h-[15px]" />

      <SubmitButton
        title="Reschedule Interview"
        onSubmitHandler={onSubmit}
        className="font-[400] text-white md:hover:bg-[white] md:hover:text-secondary hover:border-primary"
        disabled={loading}
        loading={loading}
      />
    </div>
  );
};

export default RescheduleModal;
